import { Capacitor } from '@capacitor/core';

/**
 * Tail of the queue. Every enqueued request chains off this promise, so requests
 * run one after another instead of all at once.
 */
let queueTail: Promise<unknown> = Promise.resolve();

/**
 * Queues a native permission request so only one permission dialog is open at a time.
 *
 * Android allows a single runtime permission request per Activity; a second,
 * concurrent request is discarded with `Can request only one set of permissions
 * at a time` and resolves as if the user never answered (OCST-33064).
 *
 * iOS and web are unaffected — their permission APIs queue independently — so
 * requests there run immediately and never touch the queue.
 */
export const enqueueNativePermissionRequest = <T>(request: () => Promise<T>): Promise<T> => {
  if (Capacitor.getPlatform() !== 'android') {
    return request();
  }

  // Start this request only after the previous queue entry has settled.
  const result = queueTail.then(request);

  // The queue must never stay in a rejected state, otherwise every later request
  // would be skipped. The caller still sees the real failure through `result`;
  // this `catch` only neutralizes the rejection for the next link in the queue.
  queueTail = result.catch(() => undefined);

  return result;
};
